// Conditional-logic evaluation for the form contract (visibility + requirement
// rules). Mirrors the mobile renderer's semantics so the web capture UI hides /
// requires fields the same way the device does.
//
// A `FieldCondition` references ANOTHER field by its top-level `key`. In the web
// capture UI, values live in the flat capture map keyed by the referenced field's
// `target` (e.g. `row_data.working_with_sea_weeds` → `working_with_sea_weeds`,
// `name` → `$name`), so we resolve key → target → value here. A rule may therefore
// reference a title field or the description field, not just a `row_data` column.
//
// See docs/developer-guides/2026-08-05_form-builder-mobile-contract-alignment.md §3.

import { ConditionMatch, FieldCondition, ConditionOperator, SchemaField } from "../../types/fieldForm";
import { captureKeyOf } from "./captureKeys";

/** The captured value of a field, read from the capture map by its `target` key. */
export function fieldValue(field: SchemaField, values: Record<string, unknown>): unknown {
  const key = captureKeyOf(field.target);
  return key ? values[key] : undefined;
}

/** Coerce yes/no-ish values to a boolean. */
function toBool(v: unknown): boolean {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  const s = String(v ?? "")
    .trim()
    .toLowerCase();
  return s === "true" || s === "yes" || s === "1";
}

/** Empty = null / undefined / "" / empty array. */
function isEmpty(v: unknown): boolean {
  if (v == null) return true;
  if (Array.isArray(v)) return v.length === 0;
  return String(v).trim() === "";
}

/** Loose equality that tolerates boolean ⇄ "yes"/"no"/"true" and number ⇄ string. */
function looseEqual(a: unknown, b: unknown): boolean {
  if (typeof a === "boolean" || typeof b === "boolean") return toBool(a) === toBool(b);
  if (Array.isArray(a)) return a.map(String).includes(String(b));
  return String(a ?? "") === String(b ?? "");
}

function contains(actual: unknown, needle: unknown): boolean {
  if (Array.isArray(actual)) return actual.map(String).includes(String(needle));
  return String(actual ?? "")
    .toLowerCase()
    .includes(String(needle ?? "").toLowerCase());
}

function numericCompare(actual: unknown, expected: unknown, op: ConditionOperator): boolean {
  const a = Number(actual);
  const b = Number(expected);
  if (Number.isNaN(a) || Number.isNaN(b)) return false;
  switch (op) {
    case "greater_than":
      return a > b;
    case "less_than":
      return a < b;
    case "greater_or_equal":
      return a >= b;
    case "less_or_equal":
      return a <= b;
    default:
      return false;
  }
}

/** Apply one operator to an actual value + the condition's operand. */
function applyOperator(actual: unknown, operator: ConditionOperator, expected: unknown): boolean {
  switch (operator) {
    case "equal_to":
      return looseEqual(actual, expected);
    case "not_equal_to":
      return !looseEqual(actual, expected);
    case "is_empty":
      return isEmpty(actual);
    case "is_not_empty":
      return !isEmpty(actual);
    case "contains":
      return contains(actual, expected);
    case "does_not_contain":
      return !contains(actual, expected);
    case "greater_than":
    case "less_than":
    case "greater_or_equal":
    case "less_or_equal":
      return numericCompare(actual, expected, operator);
    default:
      return true;
  }
}

/** Evaluate a single condition against the referenced field's captured value. */
export function evaluateCondition(cond: FieldCondition, fields: SchemaField[], rowData: Record<string, unknown>): boolean {
  const ref = fields.find((f) => f.key === cond.field_key);
  const actual = ref ? fieldValue(ref, rowData) : undefined;
  return applyOperator(actual, cond.operator, cond.value);
}

/** Combine a rule's conditions with AND (`all`, default) or OR (`any`). Empty → passes. */
export function passesConditions(
  conds: FieldCondition[] | undefined,
  type: ConditionMatch | undefined,
  fields: SchemaField[],
  rowData: Record<string, unknown>
): boolean {
  if (!conds || conds.length === 0) return true;
  const results = conds.map((c) => evaluateCondition(c, fields, rowData));
  return type === "any" ? results.some(Boolean) : results.every(Boolean);
}

/** Whether a field should be shown, given `hidden` + its `visible_conditions`. */
export function isFieldVisible(field: SchemaField, fields: SchemaField[], rowData: Record<string, unknown>): boolean {
  if (field.hidden) return false;
  return passesConditions(field.visible_conditions, field.visible_conditions_type, fields, rowData);
}

/** Effective required state: base `required` OR its `required_conditions` passing. */
export function isFieldRequired(field: SchemaField, fields: SchemaField[], rowData: Record<string, unknown>): boolean {
  if (field.required) return true;
  if (field.required_conditions?.length) {
    return passesConditions(field.required_conditions, field.required_conditions_type, fields, rowData);
  }
  return false;
}
